import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A lightweight custom element that manages a localization-only drawer.
 * It reuses the focus-trap and animation helpers from the main header drawer
 * but strips out navigation/link logic — it's scoped to showing the
 * localization form (country / language list).
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class LocalizationDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  // Listen for a global 'localization-drawer:open' event so external code can request the drawer open
  document.addEventListener('localization-drawer:open', this.#handleGlobalOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  document.removeEventListener('localization-drawer:open', this.#handleGlobalOpen);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  // Handle a global request to open the localization drawer
  #handleGlobalOpen = () => {
    try {
      // Close the header drawer if present to avoid nested drawers
      const header = /** @type {HTMLElement & { close?: () => void }} */ (
        document.querySelector("header-drawer")
      );
      header?.close?.();
    } catch (e) {
      // ignore
    }

    try {
      this.open();
    } catch (err) {
      window.console.log('[localization-drawer] failed to open localization drawer:', err);
      // ignore
    }
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    // Prevent the native <summary> click toggle from running after our handler.
    // The browser toggles the <details> open state as the default action for
    // summary clicks; if we don't preventDefault(), that native toggle can
    // flip the state after we programmatically open the details, which was
    // causing the drawer to immediately close when opened via a click event.
    try {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
    } catch (e) {
      // ignore
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    // Ensure the details element is marked open — some CSS relies on details[open]
    details.setAttribute('open', '');
    details.open = true;

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('localization-drawer')) {
  customElements.define('localization-drawer', LocalizationDrawer);
}

// Allow external code to open the localization drawer by dispatching
// `document.dispatchEvent(new CustomEvent('localization-drawer:open'))`.

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

export default LocalizationDrawer;
